"use client";

import { useEffect, useState } from "react";
import type { Result } from "axe-core";

type AuditState = {
  running: boolean;
  checkedAt?: Date;
  violations: Result[];
  incomplete: Result[];
  passes: number;
  siteIssues: SiteIssue[];
  error?: string;
};

type SiteIssue = { category: "SEO" | "Prestaties" | "Privacy"; message: string; target?: string };

const initialState: AuditState = { running: true, violations: [], incomplete: [], passes: 0, siteIssues: [] };

function label(result: Result): string {
  return result.id === "color-contrast" ? `Contrast: ${result.help}` : result.help;
}

function inspectPage(): SiteIssue[] {
  const issues: SiteIssue[] = [];
  if (!document.title.trim()) issues.push({ category: "SEO", message: "De pagina heeft geen paginatitel." });
  if (!document.querySelector('meta[name="description"]')?.getAttribute("content")?.trim()) issues.push({ category: "SEO", message: "De pagina heeft geen metabeschrijving." });
  const headings = document.querySelectorAll("h1");
  if (headings.length !== 1) issues.push({ category: "SEO", message: `Er hoort precies één hoofdkop (h1) te zijn; gevonden: ${headings.length}.` });
  if (document.documentElement.scrollWidth > document.documentElement.clientWidth + 1) issues.push({ category: "Prestaties", message: "De pagina is breder dan het scherm." });
  for (const image of document.querySelectorAll<HTMLImageElement>("img")) {
    const style = getComputedStyle(image);
    if (!image.hasAttribute("width") && !image.hasAttribute("height") && style.aspectRatio === "auto") {
      issues.push({ category: "Prestaties", message: "De afbeelding heeft geen vaste afmetingen of beeldverhouding en kan de pagina laten verspringen.", target: image.currentSrc || image.src });
    }
  }
  for (const resource of performance.getEntriesByType("resource") as PerformanceResourceTiming[]) {
    if (process.env.NODE_ENV !== "development" && /^https:\/\/fonts\.(?:googleapis|gstatic)\.com/iu.test(resource.name)) issues.push({ category: "Privacy", message: "De browser haalt een lettertype bij Google op in plaats van bij deze website.", target: resource.name });
  }
  issues.push(...inspectConsent());
  return issues;
}

/**
 * Consent checks (docs/cookie-consent-plan.md §4.6): what this page stores and
 * contacts compared with the consent register and the visitor's current choice.
 */
function inspectConsent(): SiteIssue[] {
  const config = window.__cmsConsentConfig;
  if (!config) return [];
  const issues: SiteIssue[] = [];
  const state = window.__cmsConsentState ?? null;
  const services = config.categories.flatMap((category) => category.services.map((service) => ({ ...service, category: category.id })));
  const allowed = (serviceId: string) => {
    const service = services.find((item) => item.id === serviceId);
    if (!service) return false;
    if (service.category === "necessary" || service.basis === "exempt") return true;
    if (state?.s && typeof state.s[serviceId] === "boolean") return state.s[serviceId];
    if (service.basis === "legitimate_interest") return state?.c[service.category as "analytics"] !== false;
    return state?.c[service.category as "analytics"] === true;
  };
  const cookieOwner = (name: string) => services.find((service) => service.cookies.some((cookie) => cookie.name.endsWith("*") ? name.startsWith(cookie.name.slice(0, -1)) : cookie.name === name));
  for (const name of document.cookie.split(";").map((part) => part.split("=")[0].trim()).filter(Boolean)) {
    if (name.startsWith("__next") || name === "cms_session") continue;
    const owner = cookieOwner(name);
    if (!owner) issues.push({ category: "Privacy", message: `Cookie "${name}" staat niet in het cookieregister. Declareer hem in de module, het thema of onder Privacy & cookies.`, target: name });
    else if (!allowed(owner.id)) issues.push({ category: "Privacy", message: `Cookie "${name}" (${owner.name}) staat er zonder toestemming van de bezoeker.`, target: name });
  }
  const origin = location.hostname;
  for (const resource of performance.getEntriesByType("resource") as PerformanceResourceTiming[]) {
    let host = "";
    try { host = new URL(resource.name).hostname; } catch { continue; }
    if (!host || host === origin) continue;
    const serviceId = config.hosts[host];
    if (!serviceId) issues.push({ category: "Privacy", message: `De pagina laadt iets van ${host}, maar die dienst staat niet in het cookieregister.`, target: resource.name });
    else if (!allowed(serviceId)) issues.push({ category: "Privacy", message: `De pagina laadt iets van ${host} zonder toestemming voor ${services.find((item) => item.id === serviceId)?.name || serviceId}.`, target: resource.name });
  }
  const blocked = (window as Window & { __cmsBlocked?: Element[] }).__cmsBlocked || [];
  if (blocked.length) issues.push({ category: "Privacy", message: `De toestemmingsbewaker hield ${blocked.length} script(s), iframe(s) of pixel(s) tegen. Gebruik ConsentGate, ConsentEmbed of loadScript uit @cms/module-api/client.`, target: blocked.map((node) => node.getAttribute("data-cms-blocked-src")).join(", ") });
  if (!config.floatingButton && config.categories.some((category) => category.id !== "necessary") && !document.querySelector(".cms-cookie-settings-link, [data-cms-cookie-settings]")) {
    issues.push({ category: "Privacy", message: "Bezoekers kunnen hun cookiekeuze nergens wijzigen: zet de zwevende cookieknop aan of plaats CookieSettingsLink in de footer." });
  }
  return issues;
}

export function DevSiteChecker() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<AuditState>(initialState);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let running = false;
    let queued = false;

    const audit = async () => {
      if (running) { queued = true; return; }
      running = true;
      setState((current) => ({ ...current, running: true, error: undefined }));
      try {
        const axe = (await import("axe-core")).default;
        axe.configure({ locale: (await import("axe-core/locales/nl.json")).default });
        const result = await axe.run(
          { exclude: [["[data-site-checker]"]] },
          { runOnly: { type: "tag", values: ["wcag2a", "wcag2aa", "wcag21a", "wcag21aa"] } },
        );
        if (!cancelled) setState({
          running: false,
          checkedAt: new Date(),
          violations: result.violations,
          incomplete: result.incomplete,
          passes: result.passes.length,
          siteIssues: inspectPage(),
        });
      } catch (error) {
        if (!cancelled) setState((current) => ({ ...current, running: false, error: "De websitecontrole kon niet worden uitgevoerd. Probeer het opnieuw." }));
      } finally {
        running = false;
        if (queued && !cancelled) { queued = false; void audit(); }
      }
    };

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => void audit(), 350);
    };
    const observer = new MutationObserver((mutations) => {
      const pageChanged = mutations.some((mutation) => {
        const element = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
        return !element?.closest("[data-site-checker]");
      });
      if (pageChanged) schedule();
    });
    observer.observe(document.body, { attributes: true, childList: true, subtree: true, characterData: true });
    void audit();
    return () => { cancelled = true; clearTimeout(timer); observer.disconnect(); };
  }, []);

  const contrast = state.violations.filter((item) => item.id === "color-contrast");
  const accessibilityIssueCount = state.violations.reduce((total, item) => total + item.nodes.length, 0);
  const issueCount = accessibilityIssueCount + state.siteIssues.length;
  const color = state.error ? "#b42318" : issueCount ? "#b54708" : "#067647";

  return (
    <aside data-site-checker aria-label="Websitecontrole" style={{ position: "relative", padding: 14, zIndex: 1, font: "13px/1.4 Segoe UI,system-ui,sans-serif", color: "#101828" }}>
      {open && (
        <div style={{ width: "min(440px, calc(100vw - 28px))", maxHeight: "min(620px, calc(100vh - 90px))", overflow: "auto", marginBottom: 8, padding: 18, border: "1px solid #d0d5dd", borderRadius: 12, background: "#fff", boxShadow: "0 12px 32px rgba(16,24,40,.2)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <strong style={{ fontSize: 16 }}>Websitecontrole</strong>
            <button type="button" onClick={() => setOpen(false)} aria-label="Websitecontrole sluiten" style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 20 }}>×</button>
          </div>
          <p style={{ margin: "6px 0 14px", color: "#475467" }}>Automatische controles op toegankelijkheid, vindbaarheid, prestaties en privacy.</p>
          {state.error ? <p style={{ color: "#b42318" }}>{state.error}</p> : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
                <Summary value={state.passes} caption="regels in orde" color="#067647" />
                <Summary value={accessibilityIssueCount} caption="toegankelijkheidsproblemen" color={accessibilityIssueCount ? "#b42318" : "#067647"} />
                <Summary value={contrast.reduce((total, item) => total + item.nodes.length, 0)} caption="contrast" color={contrast.length ? "#b42318" : "#067647"} />
              </div>
              {state.siteIssues.map((item, index) => (
                <div key={`${item.category}-${index}`} style={{ padding: "10px 0", borderTop: "1px solid #eaecf0" }}>
                  <strong>{item.category}: {item.message}</strong>
                  {item.target && <code style={{ display: "block", marginTop: 4, overflowWrap: "anywhere", color: "#475467" }}>{item.target}</code>}
                </div>
              ))}
              {state.violations.length === 0 && state.siteIssues.length === 0 ? <p style={{ color: "#067647", fontWeight: 700 }}>Alle automatische ontwikkelcontroles zijn in orde.</p> : state.violations.map((item) => (
                <details key={item.id} style={{ padding: "10px 0", borderTop: "1px solid #eaecf0" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 700 }}>{label(item)} ({item.nodes.length})</summary>
                  <p style={{ margin: "6px 0", color: "#475467" }}>{item.description}</p>
                  <ul style={{ margin: "6px 0", paddingLeft: 20 }}>
                    {item.nodes.slice(0, 10).map((node, index) => <li key={`${item.id}-${index}`}><code style={{ overflowWrap: "anywhere" }}>{node.target.join(" ")}</code></li>)}
                  </ul>
                  <a href={item.helpUrl} target="_blank" rel="noreferrer">Uitleg over het oplossen</a>
                </details>
              ))}
              {state.incomplete.length > 0 && <p style={{ color: "#475467" }}>{state.incomplete.length} regels moeten handmatig worden gecontroleerd.</p>}
              {state.checkedAt && <small style={{ color: "#667085" }}>Gecontroleerd om {state.checkedAt.toLocaleTimeString("nl-NL")}</small>}
            </>
          )}
        </div>
      )}
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} style={{ border: 0, borderRadius: 999, padding: "9px 14px", background: color, color: "#fff", boxShadow: "0 4px 14px rgba(0,0,0,.3)", cursor: "pointer", fontWeight: 700 }}>
        {state.running ? "Controleren…" : issueCount ? `Controle: ${issueCount} ${issueCount === 1 ? "probleem" : "problemen"}` : "Controle: in orde"}
      </button>
    </aside>
  );
}

function Summary({ value, caption, color }: { value: number; caption: string; color: string }) {
  return <div style={{ padding: 9, borderRadius: 8, background: "#f2f4f7", textAlign: "center" }}><strong style={{ display: "block", color, fontSize: 18 }}>{value}</strong><small>{caption}</small></div>;
}
